import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Formik } from 'formik';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';

import routesClient from '../../../../clients/routes';

import { fromForm, toForm } from '../../utils/formData';
import validationSchema from '../../utils/validationSchema';
import prepareFiles, { prepareMultipleFiles } from '../../utils/prepareFiles';

import Form from '../../components/Form';
import CoordinateAudioGuidesField from '../../components/Form/components/CoordinateAudioGuidesField';
import LanguageSelectField from '../../../Objects/components/Form/components/LanguageSelectField';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(2),
  },
  sidebar: {
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
    },
  },
  languageSelect: {
    minWidth: 200,
  },
}));

const NewRoute = () => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values) => {
    try {
      const GXPFileId = values.GXPFile ? await prepareFiles(values.GXPFile) : null;
      const VideoId = values.Video ? await prepareFiles(values.Video) : null;
      const ImagesIds = (values.Images && values.Images.length > 0)
        ? await prepareMultipleFiles(values.Images)
        : [];

      // Prepare audio files for coordinate guides
      const preparedCoordinateGuides = await Promise.all(
        (values.CoordinateAudioGuides || []).map(async (guide) => {
          const audioFileId = guide.AudioFile && !guide.AudioFile.id
            ? await prepareFiles(guide.AudioFile)
            : (guide.AudioFile?.id || null);
          const objectId = guide.object != null && guide.object !== ''
            ? (typeof guide.object === 'object' && guide.object != null && 'id' in guide.object ? guide.object.id : guide.object)
            : null;
          const subcategoryId =
            guide.subcategory != null && guide.subcategory !== ''
              ? (typeof guide.subcategory === 'object' &&
                  guide.subcategory != null &&
                  'id' in guide.subcategory
                  ? guide.subcategory.id
                  : guide.subcategory)
              : null;
          return {
            ...guide,
            AudioFile: audioFileId,
            object: objectId,
            subcategory: subcategoryId,
          };
        })
      );
      
      const formData = fromForm({ 
        ...values, 
        GXPFile: GXPFileId,
        Video: VideoId,
        Images: ImagesIds,
        CoordinateAudioGuides: preparedCoordinateGuides,
      });
      
      const { locale, ...routeData } = formData;
      
      // Always create default locale (LT) entry first
      const ltData = locale === 'lt-LT' ? routeData : {
        ...routeData,
        Title: '',
        Description: '',
      };
      
      const { data: { documentId: id } } = await routesClient.create(ltData, 'lt-LT');

      // If current locale is EN-EU and has data, create EN-EU localization
      if (locale === 'en-EU' && (routeData.Title || routeData.Description)) {
        try {
          await routesClient.createLocalization(id, routeData, 'en-EU');
        } catch (error) {
          console.error('Failed to create English localization:', error);
          enqueueSnackbar(
            'Maršrutas sukurtas, bet nepavyko sukurti angliško vertimo. Galite pridėti jį vėliau redaguojant maršrutą.',
            { variant: 'warning' }
          );
        }
      }

      enqueueSnackbar('Maršrutas sukurtas', { variant: 'success' });

      history.push(path.replace('/new', `/${id}`));
    } catch (error) {
      console.error('Error creating route:', error);
      enqueueSnackbar('Nepavyko sukurti maršruto. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  return (
    <div>
      <Formik
        initialValues={{
          locale: 'lt-LT',
          Title: '',
          Description: '',
          RouteStartCoordinates: null,
          GXPFile: null,
          Video: null,
          Images: [],
          VideoURL: '',
          Region: null,
          District: null,
          Length: null,
          Duration: '',
          VirtualAudioGuideExists: false,
          Paid: false,
          PaidAmount: null,
          SuitableFor: null,
          TravelMethod: null,
          objects: [],
          CoordinateAudioGuides: [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount={false}
      >
        {(formikProps) => (
          <>
            <Box className={classes.header}>
              <Typography variant="h5">
                Naujas maršrutas
              </Typography>
              <LanguageSelectField
                name="locale"
                label="Kalba / Language"
                className={classes.languageSelect}
              />
            </Box>
            <Grid container spacing={3} className={classes.container}>
            <Grid item xs={12} md={8}>
              <Form {...formikProps} />
            </Grid>
            <Grid item xs={12} md={4}>
              <div className={classes.sidebar}>
                <CoordinateAudioGuidesField
                  name="CoordinateAudioGuides"
                  gpxFile={formikProps.values.GXPFile}
                  disabled={formikProps.isSubmitting}
                  locale={formikProps.values.locale}
                />
              </div>
            </Grid>
          </Grid>
          </>
        )}
      </Formik>
    </div>
  );
};

export default NewRoute;
